import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ArrowRight, Ban, Banknote, CircleCheck, CreditCard, ExternalLink, FileText, Globe, History, MapPin, Pencil, Phone,
  ReceiptText, RefreshCw, StickyNote, TriangleAlert, Undo2, User, X,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import { useAuth } from '@/hooks/useAuth';
import { useLabels } from '@/contexts/WorkspaceContext';
import {
  nextStatus, Order, OrderPaymentMethod, OrderStatus, ordersService, ORDER_STATUS_LABEL,
} from '@/services/orders.service';
import { formatMoney } from '@/pages/catalog/shared';
import { dateTimeOf, ElapsedPill, NEXT_LABEL, PaymentBadge, StatusBadge, timeOf, TypeBadge, useNow } from './orderShared';

const CANCEL_REASONS = ['Guest left', 'Out of stock', 'Duplicate order', 'Ordered by mistake'];
const PAY_METHODS: Array<{ value: OrderPaymentMethod; label: string; icon: typeof Banknote }> = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'online', label: 'Online', icon: Globe },
];

interface Props {
  orderUid: string | null;
  onClose: () => void;
  /** Called with the fresh order after every change, so the board can update in place. */
  onChanged: (o: Order) => void;
}

export default function OrderDrawer({ orderUid, onClose, onChanged }: Props) {
  const t = useLabels();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const now = useNow();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = async (uid: string) => {
    setLoading(true);
    setFailed(false);
    try {
      setOrder(await ordersService.get(uid));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOrder(null);
    setCancelOpen(false);
    setReason('');
    if (orderUid) void load(orderUid);
  }, [orderUid]);

  useEffect(() => {
    if (!orderUid) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [orderUid, onClose]);

  if (!orderUid) return null;

  const run = async (key: string, fn: () => Promise<Order>, success?: string) => {
    setBusy(key);
    try {
      const next = await fn();
      setOrder(next);
      onChanged(next);
      if (success) toast.success(success);
      return next;
    } catch (e: any) {
      if (!e?.response) toast.error('Something went wrong — try again');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const setStatus = (s: Exclude<OrderStatus, 'cancelled'>) =>
    order && run(`status-${s}`, () => ordersService.setStatus(order.orderUid, s), `#${order.number} is ${ORDER_STATUS_LABEL[s].toLowerCase()}`);

  const invoice = async () => {
    if (!order) return;
    setBusy('invoice');
    try {
      const r = await ordersService.invoice(order.orderUid);
      setOrder(r.order);
      onChanged(r.order);
      toast.success(r.created ? 'Invoice created' : 'Invoice already exists');
      navigate(`/documents/${r.documentUid}`);
    } catch (e: any) {
      if (!e?.response) toast.error('Could not create the invoice');
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!order || !reason.trim()) return;
    const done = await run('cancel', () => ordersService.cancel(order.orderUid, reason.trim()), `#${order.number} cancelled`);
    if (done) setCancelOpen(false);
  };

  const open = order && order.status !== 'completed' && order.status !== 'cancelled';
  const next = order ? nextStatus(order) : null;
  const editable = order && (order.status === 'new' || order.status === 'preparing');
  const flow: Array<Exclude<OrderStatus, 'cancelled'>> = order?.type === 'delivery'
    ? ['new', 'preparing', 'ready', 'out_for_delivery', 'completed']
    : ['new', 'preparing', 'ready', 'completed'];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={order ? `Order #${order.number}` : 'Order'}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-[480px] flex-col border-l border-border bg-surface shadow-elevated animate-fade-in">
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          {order ? (
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-xl font-bold tracking-tight text-text-primary">#{order.number}</h2>
                <StatusBadge status={order.status} />
                <ElapsedPill order={order} now={now} />
              </div>
              <p className="mt-1 text-xs text-text-muted">Placed {dateTimeOf(order.placedAt)}</p>
            </div>
          ) : (
            <div className="skeleton h-8 w-40 rounded-lg" />
          )}
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {failed ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger"><TriangleAlert size={22} /></span>
              <p className="text-sm font-semibold text-text-primary">Couldn’t load this order</p>
              <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />} onClick={() => void load(orderUid)}>Retry</Button>
            </div>
          ) : !order || loading ? (
            <div className="space-y-3 p-5">
              <div className="skeleton h-20 rounded-xl" /><div className="skeleton h-40 rounded-xl" /><div className="skeleton h-24 rounded-xl" />
            </div>
          ) : (
            <div className="space-y-5 p-5">
              {open && (
                <section>
                  <div className="mb-2 flex items-center gap-1 overflow-x-auto">
                    {flow.map((s, i) => {
                      const reached = flow.indexOf(order.status as Exclude<OrderStatus, 'cancelled'>) >= i;
                      const current = order.status === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          disabled={current || !!busy}
                          onClick={() => void setStatus(s)}
                          title={current ? 'Current status' : `Move to ${ORDER_STATUS_LABEL[s].toLowerCase()}`}
                          className={clsx(
                            'flex-1 whitespace-nowrap rounded-lg px-2 py-1.5 text-2xs font-semibold transition-colors',
                            current ? 'bg-primary text-on-primary' : reached ? 'bg-primary-soft text-primary hover:brightness-110' : 'bg-surface-alt text-text-muted hover:text-text-primary',
                          )}
                        >
                          {ORDER_STATUS_LABEL[s]}
                        </button>
                      );
                    })}
                  </div>
                  {next && (
                    <Button className="w-full" size="lg" loading={busy === `status-${next}`} disabled={!!busy} iconRight={<ArrowRight size={16} />} onClick={() => void setStatus(next)}>
                      {NEXT_LABEL[next as keyof typeof NEXT_LABEL]}
                    </Button>
                  )}
                </section>
              )}

              {order.status === 'cancelled' && (
                <div className="rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
                  <div className="font-semibold">Cancelled {timeOf(order.cancelledAt)}</div>
                  {order.cancelledReason && <div className="mt-0.5 text-xs">{order.cancelledReason}</div>}
                </div>
              )}

              <section className="rounded-2xl border border-border p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <TypeBadge order={order} />
                  {editable && (
                    <Link to={`/orders/${order.orderUid}/edit`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                      <Pencil size={12} /> Edit order
                    </Link>
                  )}
                </div>
                <dl className="space-y-2 text-sm">
                  {(order.customerName || order.contactUid) && (
                    <div className="flex items-center gap-2">
                      <User size={14} className="shrink-0 text-text-muted" />
                      {order.contactUid ? (
                        <Link to={`/contacts/${order.contactUid}`} className="font-medium text-text-primary hover:text-primary">{order.customerName || `View ${t('contacts', 'singular').toLowerCase()}`}</Link>
                      ) : <span className="font-medium text-text-primary">{order.customerName}</span>}
                    </div>
                  )}
                  {order.customerPhone && (
                    <div className="flex items-center gap-2"><Phone size={14} className="shrink-0 text-text-muted" /><a href={`tel:${order.customerPhone}`} className="text-text-secondary hover:text-primary">{order.customerPhone}</a></div>
                  )}
                  {order.deliveryAddress && (
                    <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0 text-text-muted" /><span className="text-text-secondary">{order.deliveryAddress}</span></div>
                  )}
                  {order.notes && (
                    <div className="flex items-start gap-2"><StickyNote size={14} className="mt-0.5 shrink-0 text-text-muted" /><span className="whitespace-pre-wrap text-text-secondary">{order.notes}</span></div>
                  )}
                  {!order.customerName && !order.customerPhone && !order.deliveryAddress && !order.notes && !order.contactUid && (
                    <p className="text-xs text-text-muted">No guest details on this order.</p>
                  )}
                </dl>
              </section>

              <section className="rounded-2xl border border-border">
                <ul className="divide-y divide-border">
                  {order.items.map((l) => (
                    <li key={l.lineUid} className="flex items-start gap-3 px-4 py-2.5">
                      <span className="mt-0.5 flex h-6 min-w-6 items-center justify-center rounded-md bg-surface-alt px-1.5 text-xs font-bold tabular-nums text-text-primary">{l.quantity}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-text-primary">{l.name}</div>
                        {l.notes && <div className="text-xs italic text-warning">{l.notes}</div>}
                        <div className="text-2xs text-text-muted">{formatMoney(l.unitPrice, order.currency)} each</div>
                      </div>
                      <span className="text-sm font-semibold tabular-nums text-text-primary">{formatMoney(l.lineTotal, order.currency)}</span>
                    </li>
                  ))}
                </ul>
                <dl className="space-y-1.5 border-t border-border bg-surface-alt/50 px-4 py-3 text-sm">
                  <Row label="Subtotal" value={formatMoney(order.subtotal, order.currency)} />
                  {order.discount > 0 && <Row label="Discount" value={`− ${formatMoney(order.discount, order.currency)}`} />}
                  {order.taxRatePct > 0 && (
                    <Row label={`VAT ${order.taxRatePct}%${order.vatInclusive ? ' (included)' : ''}`} value={formatMoney(order.taxAmount, order.currency)} muted={order.vatInclusive} />
                  )}
                  <div className="flex items-center justify-between pt-1.5">
                    <dt className="font-semibold text-text-primary">Total</dt>
                    <dd className="font-display text-lg font-bold tabular-nums text-text-primary">{formatMoney(order.total, order.currency)}</dd>
                  </div>
                </dl>
              </section>

              <section className="rounded-2xl border border-border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-text-primary">Payment</h3>
                  <PaymentBadge order={order} />
                </div>
                {order.paymentStatus === 'unpaid' && order.status !== 'cancelled' ? (
                  <div className="grid grid-cols-3 gap-2">
                    {PAY_METHODS.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        disabled={!!busy}
                        onClick={() => void run(`pay-${m.value}`, () => ordersService.pay(order.orderUid, m.value), `Paid by ${m.label.toLowerCase()}`)}
                        className="flex flex-col items-center gap-1 rounded-xl border border-border px-2 py-3 text-xs font-semibold text-text-primary transition-colors hover:border-success hover:bg-success-soft hover:text-success disabled:opacity-50"
                      >
                        <m.icon size={18} />
                        {busy === `pay-${m.value}` ? 'Saving…' : m.label}
                      </button>
                    ))}
                  </div>
                ) : order.paymentStatus === 'paid' ? (
                  <div className="flex items-center justify-between gap-3">
                    <p className="flex items-center gap-1.5 text-xs text-text-muted"><CircleCheck size={14} className="text-success" /> Paid {dateTimeOf(order.paidAt)}</p>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" iconLeft={<Undo2 size={13} />} loading={busy === 'refund'}
                        onClick={() => { if (window.confirm(`Refund ${formatMoney(order.total, order.currency)} on #${order.number}?`)) void run('refund', () => ordersService.refund(order.orderUid), 'Marked as refunded'); }}>
                        Refund
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-text-muted">{order.paymentStatus === 'refunded' ? 'The payment was refunded.' : 'No payment taken.'}</p>
                )}
              </section>

              <section className="flex flex-wrap gap-2">
                {order.invoiceDocumentUid ? (
                  <Link to={`/documents/${order.invoiceDocumentUid}`} className="inline-flex h-10 items-center gap-2 rounded-lg border border-border-strong px-4 text-sm font-semibold text-text-primary hover:border-primary hover:bg-surface-alt">
                    <FileText size={15} /> Open invoice <ExternalLink size={13} className="text-text-muted" />
                  </Link>
                ) : order.status !== 'cancelled' && (
                  <Button variant="secondary" iconLeft={<ReceiptText size={15} />} loading={busy === 'invoice'} disabled={!!busy} onClick={() => void invoice()}>
                    Create invoice
                  </Button>
                )}
                {open && !cancelOpen && (
                  <Button variant="ghost" className="text-danger hover:text-danger" iconLeft={<Ban size={15} />} onClick={() => setCancelOpen(true)}>
                    Cancel order
                  </Button>
                )}
              </section>

              {cancelOpen && (
                <section className="rounded-2xl border border-danger/30 p-4">
                  <h3 className="text-sm font-semibold text-text-primary">Why is it cancelled?</h3>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {CANCEL_REASONS.map((r) => (
                      <button key={r} type="button" onClick={() => setReason(r)}
                        className={clsx('rounded-full border px-2.5 py-1 text-xs font-medium', reason === r ? 'border-danger bg-danger-soft text-danger' : 'border-border text-text-secondary hover:border-border-strong')}>
                        {r}
                      </button>
                    ))}
                  </div>
                  <Textarea className="mt-3" rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Or type a reason" />
                  <div className="mt-3 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setCancelOpen(false); setReason(''); }}>Keep order</Button>
                    <Button variant="danger" size="sm" disabled={!reason.trim()} loading={busy === 'cancel'} onClick={() => void cancel()}>Cancel order</Button>
                  </div>
                </section>
              )}

              <section>
                <h3 className="mb-3 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted"><History size={12} /> Timeline</h3>
                <ol className="relative space-y-3 border-l border-border pl-4">
                  {[...order.timeline].reverse().map((e, i) => (
                    <li key={`${e.at}-${i}`} className="relative">
                      <span className={clsx('absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-surface', timelineDot(e.action))} />
                      <div className="text-[13px] font-medium text-text-primary">{timelineText(e)}</div>
                      <div className="text-2xs text-text-muted">{dateTimeOf(e.at)}</div>
                    </li>
                  ))}
                </ol>
              </section>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-text-muted">{label}</dt>
      <dd className={clsx('tabular-nums', muted ? 'text-text-muted' : 'text-text-primary')}>{value}</dd>
    </div>
  );
}

function timelineDot(action: Order['timeline'][number]['action']) {
  switch (action) {
    case 'paid': return 'bg-success';
    case 'cancelled': return 'bg-danger';
    case 'refunded': return 'bg-text-muted';
    case 'invoiced': return 'bg-info';
    case 'notified': return 'bg-success';
    case 'updated': return 'bg-border-strong';
    default: return 'bg-primary';
  }
}

function timelineText(e: Order['timeline'][number]) {
  switch (e.action) {
    case 'placed': return 'Order placed';
    case 'status': return e.status ? `Moved to ${ORDER_STATUS_LABEL[e.status].toLowerCase()}` : 'Status changed';
    case 'updated': return 'Order edited';
    case 'paid': return `Paid${e.note ? ` by ${e.note}` : ''}`;
    case 'refunded': return 'Payment refunded';
    case 'cancelled': return `Cancelled${e.note ? ` — ${e.note}` : ''}`;
    case 'invoiced': return 'Invoice created';
    case 'notified': return e.note ?? 'WhatsApp update';
    default: return e.action;
  }
}
